import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext";
import { getData, postData, serverURL } from "../../../../services/Fetchnodeservices";

// ===================== ICONS =====================
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const SparklesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 3L14 8L19 10L14 12L12 17L10 12L5 10L10 8L12 3Z"/>
    <path d="M19 4L20 7L23 8L20 9L19 12L18 9L15 8L18 7L19 4Z"/>
  </svg>
);

const TrendingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M23 6l-9.5 9.5-5-5L1 18"/>
    <path d="M17 6h6v6"/>
  </svg>
);

const WalletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 6v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"/>
    <path d="M22 10h-4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h4"/>
    <circle cx="18" cy="14" r="1" fill="currentColor"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M23 4v6h-6"/>
    <path d="M1 20v-6h6"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
  </svg>
);

const RestaurantIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 3h7v7H3z"/>
    <path d="M14 3h7v7h-7z"/>
    <path d="M14 14h7v7h-7z"/>
    <path d="M3 14h7v7H3z"/>
  </svg>
);

const RiderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="5" cy="18" r="3"/>
    <circle cx="19" cy="18" r="3"/>
    <polyline points="12 19 12 15 9 9 17 9"/>
  </svg>
);

// ===================== STATUS BADGE (Theme-Aware) =====================
const StatusBadge = ({ status, theme }) => {
  const getStatusConfig = (adminStatus) => {
    const statusMap = {
      approved: { bg: `${theme.success}1a`, color: theme.success, label: 'Approved' },
      pending: { bg: `${theme.warning}1a`, color: theme.warning, label: 'Pending' },
      rejected: { bg: `${theme.error || theme.danger}1a`, color: theme.error || theme.danger, label: 'Rejected' },
      processing: { bg: `${theme.primary}1a`, color: theme.primary, label: 'Processing' },
      bank_exported: { bg: `${theme.primary}1a`, color: theme.primary, label: 'Bank file exported' },
      bank_file: { bg: `${theme.primary}1a`, color: theme.primary, label: 'Bank file exported' },
      paid: { bg: `${theme.success}1a`, color: theme.success, label: 'Paid' },
    };
    return statusMap[adminStatus?.toLowerCase()] || statusMap.pending;
  };
  
  const c = getStatusConfig(status);
  
  return (
    <span style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '6px', 
      padding: '4px 12px', 
      borderRadius: '20px', 
      fontSize: '11px', 
      fontWeight: '600', 
      background: c.bg, 
      color: c.color 
    }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
      {c.label}
    </span>
  );
};

// ===================== ACTION BUTTONS (Theme-Aware) =====================
const ActionButtons = ({ row, onReject, onView, theme }) => {
  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      {row.admin_status?.toLowerCase() === 'pending' && (
        <>
          <button
            onClick={() => onReject(row)}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: 'none',
              background: `${theme.error || theme.danger}1a`,
              color: theme.error || theme.danger,
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = `${theme.error || theme.danger}2a`}
            onMouseLeave={(e) => e.currentTarget.style.background = `${theme.error || theme.danger}1a`}
          >
            <XIcon /> Reject
          </button>
        </>
      )}
      <button
        onClick={() => onView(row)}
        style={{
          padding: '6px 12px',
          borderRadius: '8px',
          border: `1px solid ${theme.border}`,
          background: 'transparent',
          color: theme.textSecondary,
          cursor: 'pointer',
          fontSize: '11px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.surfaceLighter}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <EyeIcon /> View
      </button>
    </div>
  );
};

// ===================== STAT CARD (Theme-Aware) =====================
const StatCard = ({ icon, title, value, subtitle, color, theme }) => {
  return (
    <div style={{
      background: theme.surface,
      borderRadius: '20px',
      padding: '20px',
      border: `1px solid ${theme.border}`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}20`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          background: `linear-gradient(135deg, ${color}20, ${color}10)`,
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color
        }}>
          {icon}
        </div>
        {subtitle && (
          <span style={{
            fontSize: '11px',
            color: theme.textMuted,
            background: theme.surfaceLighter,
            padding: '4px 10px',
            borderRadius: '20px'
          }}>
            {subtitle}
          </span>
        )}
      </div>
      <div>
        <p style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '8px' }}>{title}</p>
        <p style={{ fontSize: '28px', fontWeight: '700', color: theme.textPrimary, margin: 0 }}>{value}</p>
      </div>
    </div>
  );
};

// ===================== WITHDRAWALS TABLE (Theme-Aware) =====================
const WithdrawalsTable = ({ withdrawals, loading, selectedIds, onToggleSelection, onReject, onView, theme }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const withdrawalsList = Array.isArray(withdrawals) ? withdrawals : [];

  const filteredWithdrawals = withdrawalsList.filter(w => {
    const name = w.restaurant_name || w.rider_name || '';
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          w.id?.toString().includes(searchTerm) ||
                          w.restaurant_id?.toString().includes(searchTerm) ||
                          w.rider_id?.toString().includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || w.admin_status?.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  const totalPages = Math.ceil(filteredWithdrawals.length / itemsPerPage);
  const paginatedWithdrawals = filteredWithdrawals.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: theme.textSecondary }}>
        <div style={{ 
          width: '48px', 
          height: '48px', 
          border: `3px solid ${theme.border}`, 
          borderTopColor: theme.primary, 
          borderRadius: '50%', 
          margin: '0 auto 16px auto', 
          animation: 'spin 1s linear infinite' 
        }} />
        <p>Loading withdrawals...</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    if (status === 'approved') return theme.success;
    if (status === 'pending') return theme.warning;
    if (status === 'rejected') return theme.error || theme.danger;
    return theme.primary;
  };
  // Old exports without a batch record are safe to re-export. Do not allow a row
  // that is attached to a real bank batch to be selected again.
  const isBatchSelectable = row => ['pending', 'bank_exported', 'bank_file'].includes(row.admin_status?.toLowerCase()) && !row.bank_batch_code;
  const pendingOnPage = paginatedWithdrawals.filter(isBatchSelectable);
  const allPendingSelected = pendingOnPage.length > 0 && pendingOnPage.every(row => selectedIds.includes(row.id));

  return (
    <div style={{ 
      background: theme.surface, 
      borderRadius: '32px', 
      overflow: 'hidden', 
      boxShadow: `0 25px 50px -12px ${theme.primary}15`, 
      border: `1px solid ${theme.border}` 
    }}>
      {/* Header */}
      <div style={{ 
        padding: '20px 24px', 
        borderBottom: `1px solid ${theme.border}`, 
        background: theme.surfaceLighter,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            padding: '8px 16px', 
            border: `1px solid ${theme.border}`, 
            borderRadius: '40px', 
            backgroundColor: theme.surface 
          }}>
            <SearchIcon style={{ color: theme.textMuted, width: '14px', height: '14px' }} />
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '13px',
                color: theme.textPrimary,
                width: '180px'
              }}
            />
          </div>
          
          {/* Status Filter */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            padding: '8px 16px', 
            border: `1px solid ${theme.border}`, 
            borderRadius: '40px', 
            backgroundColor: theme.surface 
          }}>
            <FilterIcon style={{ color: theme.textMuted, width: '14px', height: '14px' }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '13px',
                color: theme.textPrimary,
                cursor: 'pointer'
              }}
            >
              <option value="all">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
              <option value="processing">Processing</option>
            </select>
          </div>
        </div>
        
        <button 
          onClick={() => {
            setSearchTerm('');
            setStatusFilter('all');
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '40px',
            border: `1px solid ${theme.border}`,
            background: 'transparent',
            color: theme.textSecondary,
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.surfaceLighter}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <RefreshIcon /> Clear Filters
        </button>
      </div>

      {/* Table */}
      {filteredWithdrawals.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 40px', color: theme.textMuted }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: theme.surfaceLighter, 
            borderRadius: '40px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            margin: '0 auto 20px auto' 
          }}>
            <WalletIcon style={{ width: '40px', height: '40px', opacity: 0.5 }} />
          </div>
          <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: theme.textPrimary }}>No withdrawals found</p>
          <p style={{ fontSize: '13px' }}>No withdrawal requests match your search criteria</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.surfaceLighter, borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ padding: '14px 10px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    onChange={() => pendingOnPage.forEach(row => onToggleSelection(row.id, !allPendingSelected))}
                    title="Select pending withdrawals on this page"
                  />
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>ID</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Name / ID</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Amount</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Remaining Balance</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Status</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Order IDs</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Request Date</th>
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedWithdrawals.map((row, idx) => {
                const displayName = row.restaurant_name || row.rider_name || 'Unknown';
                const entityId = row.restaurant_id || row.rider_id || '';
                return (
                  <tr 
                    key={idx} 
                    style={{ 
                      borderBottom: `1px solid ${theme.border}`, 
                      transition: 'background 0.2s ease' 
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || `${theme.primary}08`}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                      {isBatchSelectable(row) ? (
                        <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={(event) => onToggleSelection(row.id, event.target.checked)} />
                      ) : '—'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: theme.primary, fontFamily: 'monospace' }}>
                      #{row.id}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: theme.textPrimary }}>{displayName}</div>
                        {entityId && <div style={{ fontSize: '10px', color: theme.textMuted, marginTop: '2px' }}>ID: {entityId}</div>}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontSize: '15px', fontWeight: '700', color: getStatusColor(row.admin_status) }}>
                        ₹{parseFloat(row.total_amount || 0).toLocaleString()}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: theme.textPrimary }}>
                      ₹{parseFloat(row.remaining_balance || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <StatusBadge status={row.admin_status} theme={theme} />
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '12px', color: theme.textSecondary }}>
                      {row.order_ids || '-'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '12px', color: theme.textMuted }}>
                      {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <ActionButtons row={row} onReject={onReject} onView={onView} theme={theme} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          padding: '14px 20px', 
          borderTop: `1px solid ${theme.border}`, 
          background: theme.surfaceLighter,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <span style={{ fontSize: '12px', color: theme.textSecondary }}>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredWithdrawals.length)} of {filteredWithdrawals.length} entries
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                borderRadius: '40px',
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: currentPage === 1 ? theme.textMuted : theme.textPrimary,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease'
              }}
            >
              <ChevronLeftIcon /> Prev
            </button>
            <span style={{ padding: '6px 12px', fontSize: '12px', color: theme.textPrimary }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                borderRadius: '40px',
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: currentPage === totalPages ? theme.textMuted : theme.textPrimary,
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease'
              }}
            >
              Next <ChevronRightIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== VIEW DETAILS MODAL (Theme-Aware) =====================
const ViewDetailsModal = ({ withdrawal, onClose, theme }) => {
  if (!withdrawal) return null;

  const displayName = withdrawal.restaurant_name || withdrawal.rider_name || 'Unknown';
  const entityId = withdrawal.restaurant_id || withdrawal.rider_id || '';

  const getStatusColor = (status) => {
    if (status === 'approved') return theme.success;
    if (status === 'pending') return theme.warning;
    if (status === 'rejected') return theme.error || theme.danger;
    return theme.primary;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: theme.surface,
        borderRadius: '24px',
        width: '100%',
        maxWidth: '480px',
        overflow: 'hidden',
        boxShadow: `0 25px 50px -12px ${theme.primary}30`,
        border: `1px solid ${theme.border}`
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${theme.border}`,
          background: theme.surfaceLighter,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: theme.textPrimary, margin: 0 }}>Withdrawal Details</h3>
          <button 
            onClick={onClose} 
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              color: theme.textMuted, 
              fontSize: '22px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.error || theme.danger}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.textMuted}
          >×</button>
        </div>
        
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Request ID</label>
            <p style={{ fontSize: '15px', fontWeight: '700', color: theme.primary, margin: 0, fontFamily: 'monospace' }}>#{withdrawal.id}</p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Name</label>
            <p style={{ fontSize: '15px', fontWeight: '600', color: theme.textPrimary, margin: 0 }}>{displayName}</p>
            {entityId && <p style={{ fontSize: '12px', color: theme.textSecondary, margin: '4px 0 0' }}>Entity ID: {entityId}</p>}
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Amount</label>
            <p style={{ fontSize: '26px', fontWeight: '700', color: getStatusColor(withdrawal.admin_status), margin: 0 }}>
              ₹{parseFloat(withdrawal.total_amount || 0).toLocaleString()}
            </p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Remaining Balance</label>
            <p style={{ fontSize: '18px', fontWeight: '700', color: theme.textPrimary, margin: 0 }}>
              ₹{parseFloat(withdrawal.remaining_balance || 0).toLocaleString()}
            </p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Status</label>
            <StatusBadge status={withdrawal.admin_status} theme={theme} />
          </div>
          {withdrawal.bank_batch_code && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Bank batch / UTR</label>
              <p style={{ fontSize: '13px', color: theme.textSecondary, margin: 0 }}>{withdrawal.bank_batch_code} · {withdrawal.utr_reference || 'UTR pending'}</p>
            </div>
          )}
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Order IDs</label>
            <p style={{ fontSize: '13px', color: theme.textSecondary, margin: 0 }}>{withdrawal.order_ids || '-'}</p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Request Date</label>
            <p style={{ fontSize: '13px', color: theme.textSecondary, margin: 0 }}>
              {withdrawal.created_at ? new Date(withdrawal.created_at).toLocaleString() : '-'}
            </p>
          </div>
          
          {withdrawal.updated_at && withdrawal.updated_at !== withdrawal.created_at && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Last Updated</label>
              <p style={{ fontSize: '13px', color: theme.textSecondary, margin: 0 }}>
                {new Date(withdrawal.updated_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>
        
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button 
            onClick={onClose} 
            style={{
              padding: '8px 20px',
              borderRadius: '40px',
              border: 'none',
              background: theme.primary,
              color: 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >Close</button>
        </div>
      </div>
    </div>
  );
};

// ===================== MAIN COMPONENT (Theme-Aware) =====================
const Withdrawals = () => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('restaurant');
  const [restaurantWithdrawals, setRestaurantWithdrawals] = useState([]);
  const [riderWithdrawals, setRiderWithdrawals] = useState([]);
  const [loadingRestaurant, setLoadingRestaurant] = useState(true);
  const [loadingRider, setLoadingRider] = useState(true);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [debitAccountNumber, setDebitAccountNumber] = useState('');
  const [bankBatches, setBankBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [bankResultFile, setBankResultFile] = useState(null);
  const [bankBusy, setBankBusy] = useState(false);

  const fetchRestaurantWithdrawals = async () => {
    setLoadingRestaurant(true);
    try {
      const response = await getData("restaurant/withdrawals/all");
      console.log("Restaurant Withdrawals:", response);
      if (response && response.status === true && response.data) {
        setRestaurantWithdrawals(response.data);
      }
    } catch (error) {
      console.error("Error fetching restaurant withdrawals:", error);
    } finally {
      setLoadingRestaurant(false);
    }
  };

  const fetchRiderWithdrawals = async () => {
    setLoadingRider(true);
    try {
      const response = await getData("Payments/riderwithdrawals");
      console.log("Rider Withdrawals:", response);
      if (response && response.status === true && response.data) {
        setRiderWithdrawals(response.data);
      }
    } catch (error) {
      console.error("Error fetching rider withdrawals:", error);
    } finally {
      setLoadingRider(false);
    }
  };

  const fetchBankBatches = async (type = activeTab) => {
    const endpoint = type === 'restaurant' ? 'restaurant/withdrawals/bank-batches' : 'Payments/riderwithdrawals/bank-batches';
    const response = await getData(endpoint);
    if (response?.status) setBankBatches(response.data || []);
  };

  useEffect(() => {
    fetchRestaurantWithdrawals();
    fetchRiderWithdrawals();
    fetchBankBatches(activeTab);
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setSelectedBatchId('');
    setBankResultFile(null);
    fetchBankBatches(activeTab);
  }, [activeTab]);

  const refreshAll = () => {
    fetchRestaurantWithdrawals();
    fetchRiderWithdrawals();
    fetchBankBatches(activeTab);
  };

  const toggleSelection = (id, shouldSelect) => {
    setSelectedIds(current => shouldSelect ? [...new Set([...current, id])] : current.filter(value => value !== id));
  };

  const bankEndpoint = (action) => activeTab === 'restaurant'
    ? `restaurant/withdrawals/bank-${action}`
    : `Payments/riderwithdrawals/bank-${action}`;

  const authHeaders = () => ({ Authorization: `${localStorage.getItem('Token')}` });

  const downloadBankExcel = async () => {
    if (!selectedIds.length) return alert(`Select at least one ${activeTab === 'restaurant' ? 'vendor' : 'rider'} withdrawal.`);
    if (!/^\d{9,35}$/.test(debitAccountNumber.trim())) return alert('Enter the company IndusInd debit account number first.');
    if (!window.confirm(`Create one ${activeTab === 'restaurant' ? 'vendor' : 'rider'} bank Excel for ${selectedIds.length} selected withdrawal(s)?`)) return;
    setBankBusy(true);
    try {
      const response = await axios.post(`${serverURL}/${bankEndpoint('export')}`, {
        ids: selectedIds,
        debit_account_number: debitAccountNumber.trim(),
      }, { headers: authHeaders(), responseType: 'blob' });
      const batchCode = response.headers['x-zaptodoor-batch-code'] || `PAYOUT-${activeTab}-${Date.now()}`;
      const batchId = response.headers['x-zaptodoor-batch-id'];
      saveAs(new Blob([response.data]), `${batchCode}.xlsx`);
      // The exact newly-created batch must be selected before an upload can be
      // accepted.  Doing it here avoids an apparently-disabled Import button.
      if (batchId) setSelectedBatchId(String(batchId));
      setBankResultFile(null);
      alert(`${activeTab === 'restaurant' ? 'Vendor' : 'Rider'} bank Excel downloaded. The new batch is selected below; choose the bank result Excel and click Import.`);
      setSelectedIds([]);
      refreshAll();
    } catch (error) {
      const message = await error.response?.data?.text?.().catch(() => '') || error.message;
      alert(message || 'Could not create bank Excel.');
    } finally {
      setBankBusy(false);
    }
  };

  const importBankResult = async () => {
    if (!selectedBatchId || !bankResultFile) return alert('Choose the correct bank batch and its result Excel.');
    setBankBusy(true);
    try {
      const form = new FormData();
      form.append('file', bankResultFile);
      const response = await axios.post(`${serverURL}/${bankEndpoint(`import/${selectedBatchId}`)}`, form, { headers: authHeaders() });
      if (!response.data?.status) throw new Error(response.data?.message || 'Could not import the bank result.');
      alert(`Import complete: ${response.data.ready_to_confirm || 0} successful row(s) are ready for confirmation; ${response.data.already_paid || 0} already-paid row(s) had their UTR updated; ${response.data.failed || 0} failed.`);
      refreshAll();
    } catch (error) {
      alert(error.response?.data?.message || 'Could not import the bank result.');
    } finally {
      setBankBusy(false);
    }
  };

  const confirmBankPayouts = async () => {
    if (!selectedBatchId) return alert('Select a bank batch first.');
    if (!window.confirm('Mark only successful rows with a UTR as paid? This cannot be reversed from this screen.')) return;
    setBankBusy(true);
    try {
      const response = await postData(`${bankEndpoint(`confirm/${selectedBatchId}`)}`, {});
      if (!response?.status) throw new Error(response?.message || 'Confirmation failed.');
      alert(response.message);
      refreshAll();
    } catch (error) {
      alert(error.message || 'Could not confirm payouts.');
    } finally {
      setBankBusy(false);
    }
  };

  const handleApprove = async (withdrawal, type) => {
    if (!window.confirm(`Approve withdrawal #${withdrawal.id} for ₹${parseFloat(withdrawal.total_amount).toLocaleString()}?`)) return;
    
    try {
      const endpoint = type === 'restaurant' 
        ? 'restaurant/withdrawals/approve' 
        : 'Payments/approve_rider_withdrawal';
      
      const response = await postData(endpoint, { id: withdrawal.id });
      
      if (response && response.status === true) {
        alert('Withdrawal approved successfully!');
        fetchRestaurantWithdrawals();
        fetchRiderWithdrawals();
      } else {
        alert(response?.message || 'Failed to approve withdrawal');
      }
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      alert('Network error. Please try again.');
    }
  };

  const handleReject = async (withdrawal, type) => {
    const remarks = prompt('Enter reason for rejection:', '');
    if (remarks === null) return;
    
    try {
      const endpoint = type === 'restaurant' 
        ? 'restaurant/withdrawals/reject' 
        : 'Payments/reject_rider_withdrawal';
      
      const response = await postData(endpoint, { id: withdrawal.id, remarks });
      
      if (response && response.status === true) {
        alert('Withdrawal rejected!');
        fetchRestaurantWithdrawals();
        fetchRiderWithdrawals();
      } else {
        alert(response?.message || 'Failed to reject withdrawal');
      }
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      alert('Network error. Please try again.');
    }
  };

  const handleView = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowModal(true);
  };

  const handleBack = () => navigate(-1);

  // Calculate stats
  const restaurantStats = {
    total: restaurantWithdrawals.length,
    totalAmount: restaurantWithdrawals.reduce((sum, w) => sum + parseFloat(w.total_amount || 0), 0),
    pending: restaurantWithdrawals.filter(w => w.admin_status === 'pending').length,
    approved: restaurantWithdrawals.filter(w => w.admin_status === 'approved').length,
    rejected: restaurantWithdrawals.filter(w => w.admin_status === 'rejected').length
  };

  const riderStats = {
    total: riderWithdrawals.length,
    totalAmount: riderWithdrawals.reduce((sum, w) => sum + parseFloat(w.total_amount || 0), 0),
    pending: riderWithdrawals.filter(w => w.admin_status === 'pending').length,
    approved: riderWithdrawals.filter(w => w.admin_status === 'approved').length,
    rejected: riderWithdrawals.filter(w => w.admin_status === 'rejected').length
  };

  const currentStats = activeTab === 'restaurant' ? restaurantStats : riderStats;
  const currentWithdrawals = activeTab === 'restaurant' ? restaurantWithdrawals : riderWithdrawals;
  const currentLoading = activeTab === 'restaurant' ? loadingRestaurant : loadingRider;

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: theme.background, 
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", 
      transition: 'all 0.3s ease' 
    }}>
      <div style={{ position: 'relative', zIndex: 1, padding: '32px 24px' }}>
        {/* Header */}
        <div style={{ maxWidth: '1200px', margin: '0 auto 32px auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <button 
              onClick={handleBack} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '10px 20px',
                background: theme.surfaceLighter,
                border: `1px solid ${theme.border}`,
                borderRadius: '40px',
                color: theme.textPrimary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.surface}
              onMouseLeave={(e) => e.currentTarget.style.background = theme.surfaceLighter}
            >
              <BackIcon /> Back to Dashboard
            </button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                background: theme.surfaceLighter, 
                padding: '8px 20px', 
                borderRadius: '40px', 
                border: `1px solid ${theme.border}` 
              }}>
                <SparklesIcon />
                <span style={{ color: theme.textPrimary, fontSize: '14px', fontWeight: '500' }}>Withdrawals Management</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Switch Toggle Button */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '32px'
          }}>
            <div style={{
              background: theme.surfaceLighter,
              borderRadius: '60px',
              padding: '6px',
              display: 'inline-flex',
              gap: '8px',
              border: `1px solid ${theme.border}`
            }}>
              <button
                onClick={() => setActiveTab('restaurant')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 32px',
                  borderRadius: '50px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: activeTab === 'restaurant' ? theme.primary : 'transparent',
                  color: activeTab === 'restaurant' ? '#FFFFFF' : theme.textSecondary
                }}
              >
                <RestaurantIcon style={{ width: '18px', height: '18px' }} />
                Restaurant Withdrawals
                {activeTab === 'restaurant' && (
                  <span style={{
                    background: 'rgba(255,255,255,0.2)',
                    padding: '2px 8px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    marginLeft: '6px'
                  }}>
                    {restaurantStats.total}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('rider')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 32px',
                  borderRadius: '50px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: activeTab === 'rider' ? theme.primary : 'transparent',
                  color: activeTab === 'rider' ? '#FFFFFF' : theme.textSecondary
                }}
              >
                <RiderIcon style={{ width: '18px', height: '18px' }} />
                Rider Withdrawals
                {activeTab === 'rider' && (
                  <span style={{
                    background: 'rgba(255,255,255,0.2)',
                    padding: '2px 8px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    marginLeft: '6px'
                  }}>
                    {riderStats.total}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Stats Cards - 4 cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <StatCard 
              icon={<WalletIcon />}
              title="Total Withdrawals"
              value={currentStats.total}
              color={theme.primary}
              theme={theme}
            />
            <StatCard 
              icon={<TrendingIcon />}
              title="Total Amount"
              value={`₹${currentStats.totalAmount.toLocaleString()}`}
              color={theme.success}
              theme={theme}
            />
            <StatCard 
              icon={<CheckIcon />}
              title="Approved"
              value={currentStats.approved}
              color={theme.success}
              subtitle="✓"
              theme={theme}
            />
            <StatCard 
              icon={<XIcon />}
              title="Pending"
              value={currentStats.pending}
              color={theme.warning}
              subtitle="⏳"
              theme={theme}
            />
          </div>

          {/* Separate bank-upload payout workflow: vendor and rider batches never mix. */}
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '22px', padding: '20px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, color: theme.textPrimary, fontSize: '17px' }}>{activeTab === 'restaurant' ? 'Vendor' : 'Rider'} bank payout batch</h3>
                <p style={{ margin: '5px 0 0', color: theme.textSecondary, fontSize: '12px' }}>1. Select pending rows → 2. Download Excel → 3. Upload bank result → 4. Confirm UTR rows.</p>
              </div>
              <button onClick={refreshAll} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', border: `1px solid ${theme.border}`, background: theme.surfaceLighter, color: theme.textPrimary }}><RefreshIcon /> Refresh</button>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                value={debitAccountNumber}
                onChange={(event) => setDebitAccountNumber(event.target.value.replace(/\D/g, ''))}
                placeholder="Company debit account number"
                inputMode="numeric"
                style={{ minWidth: '240px', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.background, color: theme.textPrimary }}
              />
              <button disabled={bankBusy || !selectedIds.length} onClick={downloadBankExcel} style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', cursor: bankBusy ? 'wait' : 'pointer', background: theme.primary, color: '#fff', fontWeight: 700 }}>
                Download {activeTab === 'restaurant' ? 'vendor' : 'rider'} Excel ({selectedIds.length})
              </button>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${theme.border}` }}>
              <select value={selectedBatchId} onChange={(event) => setSelectedBatchId(event.target.value)} style={{ minWidth: '230px', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.background, color: theme.textPrimary }}>
                <option value="">Select exported {activeTab === 'restaurant' ? 'vendor' : 'rider'} batch</option>
                {bankBatches.filter(batch => batch.status !== 'COMPLETED').map(batch => <option key={batch.id} value={batch.id}>{batch.batch_code} · {batch.total_rows} rows · {batch.ready_to_confirm || 0} ready</option>)}
              </select>
              <input type="file" accept=".xls,.xlsx" onChange={(event) => setBankResultFile(event.target.files?.[0] || null)} style={{ color: theme.textSecondary, maxWidth: '280px' }} />
              <button disabled={bankBusy || !selectedBatchId || !bankResultFile} onClick={importBankResult} style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', cursor: bankBusy ? 'wait' : 'pointer', background: theme.warning, color: '#fff', fontWeight: 700 }}>Import bank result</button>
              <button disabled={bankBusy || !selectedBatchId} onClick={confirmBankPayouts} style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', cursor: bankBusy ? 'wait' : 'pointer', background: theme.success, color: '#fff', fontWeight: 700 }}>Confirm successful UTRs</button>
            </div>
          </div>

          {/* Withdrawals Table */}
          <WithdrawalsTable
            withdrawals={currentWithdrawals}
            loading={currentLoading}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
            onReject={(row) => handleReject(row, activeTab)}
            onView={handleView}
            theme={theme}
          />
        </div>
      </div>

      {/* View Details Modal */}
      {showModal && (
        <ViewDetailsModal
          withdrawal={selectedWithdrawal}
          onClose={() => {
            setShowModal(false);
            setSelectedWithdrawal(null);
          }}
          theme={theme}
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Withdrawals;
